/**
 * Inline SVG icon set for the HUD. Original 24×24 line glyphs on a shared 1.7px stroke, drawn to
 * read at 18px in the toolbar — no icon font, no external assets, no third-party icon library.
 */

const PATHS = {
  new: 'M6 3h8l4 4v14H6z M14 3v4h4',
  open: 'M3 7h6l2 2h10v10H3z',
  save: 'M4 4h12l4 4v12H4z M8 4v6h8V4 M8 20v-6h8v6',
  // Save as: the save glyph with the copy's second sheet behind it.
  'save-as': 'M8 8h9l3 3v9H8z M11 8v4h6V8 M11 20v-4h6v4 M4 16V4h11',
  // Rename: a pencil over a baseline, so it reads as editing a name rather than drawing.
  rename: 'M4 20h4L18 10l-4-4L4 16z M14 6l4 4 M13 20h8',
  undo: 'M4 10h9a5 5 0 0 1 0 10H8 M4 10l4-4 M4 10l4 4',
  redo: 'M20 10h-9a5 5 0 0 0 0 10h5 M20 10l-4-4 M20 10l-4 4',
  select: 'M5 3l14 8-6 1.6L10 20z',
  plant: 'M12 21v-7 M12 14c0-4 3-7 7-7 0 4-3 7-7 7z M12 14c0-4-3-7-7-7 0 4 3 7 7 7z',
  move: 'M12 3v18 M3 12h18 M12 3l-3 3 M12 3l3 3 M12 21l-3-3 M12 21l3-3 M3 12l3-3 M3 12l3 3 M21 12l-3-3 M21 12l-3 3',
  rotate: 'M20 12a8 8 0 1 1-3-6.2 M20 4v5h-5',
  maturity: 'M4 20h16 M8 20V9 M16 20V4 M8 9l-3 3 M8 9l3 3 M16 4l-3 3 M16 4l3 3',
  duplicate: 'M9 9h11v11H9z M4 15V4h11',
  link: 'M9.5 14.5l5-5 M8 12l-2 2a3.5 3.5 0 0 0 5 5l2-2 M16 12l2-2a3.5 3.5 0 0 0-5-5l-2 2',
  group: 'M4 4h6v6H4z M14 4h6v6h-6z M4 14h6v6H4z M14 14h6v6h-6z',
  delete: 'M4 7h16 M9 7V4h6v3 M6 7l1 13h10l1-13 M10 11v6 M14 11v6',
  perspective: 'M3 8l9-5 9 5-9 5z M3 8v8l9 5 9-5V8',
  top: 'M3 6h18v12H3z M3 12h18 M9 6v12 M15 6v12',
  presentation: 'M3 17l5-6 4 4 3-4 6 6z M3 5h18v12H3z',
  focus: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M4 8V4h4 M20 8V4h-4 M4 16v4h4 M20 16v4h-4',
  sun: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M12 2v2 M12 20v2 M2 12h2 M20 12h2 M5 5l1.5 1.5 M17.5 17.5L19 19 M19 5l-1.5 1.5 M6.5 17.5L5 19',
  // Night. A waxing crescent rather than a full disc, so it never reads as the sun at 16px.
  moon: 'M20 14.6A8.6 8.6 0 0 1 9.4 4 8.6 8.6 0 1 0 20 14.6z',
  // Twilight: a half sun on the horizon with its first rays. Used for both dawn and dusk — which one
  // it is comes from the label beside it, and the colour it is drawn on.
  horizon: 'M3 19h18 M7.5 15a4.5 4.5 0 0 1 9 0z M12 4v2.6 M5.4 7.4l1.8 1.8 M18.6 7.4l-1.8 1.8 M3.2 15H5 M19 15h1.8',
  // A broadleaf in full canopy — high summer. Distinct from `plant`, the two-leaf sprout of spring.
  // Canopy, trunk, ground: interior branch strokes turn the middle to mush at the 15px it is drawn.
  tree: 'M12 20.5v-6 M12 3.5a5.8 5.8 0 0 1 0 11.6 5.8 5.8 0 0 1 0-11.6z M9 20.5h6',
  // A single turned leaf with its midrib — autumn.
  leaf: 'M20 4c-10 .6-15.4 7-14.6 15.6C14 20.4 20.4 14 20 4z M6.4 19.2l8.2-8.2',
  // Six-fold snow crystal — winter. Three axes with one pair of barbs each: more barbs than this
  // turn to mush at 15px, which is the size it is actually drawn at.
  snowflake: 'M12 2.5v19 M3.8 7.2l16.4 9.6 M20.2 7.2L3.8 16.8 M12 7L9.5 4.8 M12 7l2.5-2.2 M12 17l-2.5 2.2 M12 17l2.5 2.2',
  // A frozen sheet with a fracture through it, not a crystal — the snowflake next to it is the
  // crystal, and two six-fold stars side by side would be one control with two labels. This is the
  // material: a plate that has frozen, split, and refrozen.
  ice: 'M4.5 5.5h15v13h-15z M10 5.5l2.2 4.8-2 2.6 3.4 2.2-1.1 3.4 M15.6 5.5l-1.3 3.2 M4.5 13.4l3.9-1.1',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M16.5 16.5L21 21',
  star: 'M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.9-5.2-2.8-5.2 2.8 1-5.9L3.5 9.7l5.9-.8z',
  chevron: 'M9 5l7 7-7 7',
  close: 'M6 6l12 12 M18 6L6 18',
  layers: 'M12 3l9 5-9 5-9-5z M3 13l9 5 9-5',
  // A plan grid seen in perspective — the reference-grid toggle. Distinct from `top`, which is a
  // camera view: this one is drawn as ground receding, because that is what it lies on.
  grid: 'M3 20h18 M5 4h14l2 16H3z M8.7 4l-1.4 16 M15.3 4l1.4 16 M4.4 12h15.2 M3.9 8h16.2',
  // Contour lines running off both edges with a closed summit ring above them — the topographic
  // overlay. Deliberately not a mountain silhouette: this reads the ground from above, which is the
  // whole difference between a contour map and a picture of a hill.
  topo: 'M2 20c4-1.2 6-4.6 10-5.1s6 1.6 10 .6 M3 15.4c3.4-1.2 5.2-4.4 9-4.9s6.6 1.3 9 .4 M9 8.4c1.7-1.5 4.3-1.5 6 0-1.7 1.5-4.3 1.5-6 0z',
  filter: 'M3 5h18l-7 8v6l-4 2v-8z',
  lock: 'M6 11h12v9H6z M9 11V8a3 3 0 0 1 6 0v3',
  eye: 'M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z M12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M12 11v6 M12 7.5v.01',
  // A speech bubble with a tail and three dots — leaving feedback. Deliberately not an envelope: an
  // envelope promises a message that goes somewhere, and this one is held in the browser until a
  // transport exists (see `Feedback.ts`). A bubble says "say something", which is what is true.
  feedback: 'M4 4.8h16v10.4h-7.4L8 19.4v-4.2H4z M8.5 9.9h.01 M12 9.9h.01 M15.5 9.9h.01',
  warning: 'M12 4l9 16H3z M12 10v5 M12 17.5v.01',
  camera: 'M3 7h4l2-3h6l2 3h4v13H3z M12 9.5a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  shutter: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M12 6a6 6 0 1 0 0 12 6 6 0 0 0 0-12z',
  sparkle: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z',
  download: 'M12 3v12 M8 11l4 4 4-4 M4 19h16',
  gallery: 'M3 5h18v14H3z M3 15l5-4 4 3 3-2 6 4',
  compare: 'M12 3v18 M4 7h5v10H4z M15 7h5v10h-5z',
  // Four corners pulling apart — take this up to the whole frame.
  expand: 'M4 9V4h5 M15 4h5v5 M20 15v5h-5 M9 20H4v-5',
  // Two chevrons back to back, no shaft. Drawn small in the middle of a compare divider, where the
  // line itself already supplies the axis and a full double-headed arrow would only thicken it.
  dragx: 'M9.5 8L5.5 12l4 4 M14.5 8l4 4-4 4',
  key: 'M14 4a5 5 0 1 0-4.6 7L4 16.4V20h3.6l1-1v-2h2v-2h2l1.4-1.4A5 5 0 0 0 14 4z M16 8v.01',
  // A loaded brush over ground — the paint tool.
  paint: 'M9 14l-4 4 1.5 3 3-1.5 4-4 M12 3l9 9-3.5 3.5-9-9z M12 3l-3 3 9 9 3-3',
  // A contoured mound with a raise arrow — the sculpt tool.
  sculpt: 'M2 19h20 M4 15c3-5 6-1 8-4s5 1 8-2 M12 3v6 M12 3l-2.5 2.5 M12 3l2.5 2.5',
  // A wand held at the SketchUp-ish 45°, with a four-point spark at the tip and one small
  // companion. Distinct from `sparkle`, which is the enhance action rather than a tool.
  wand: 'M4 20l10-10 M6.4 17.6l-1.9 1.9 M17 3l.9 2.6L20.5 6.5l-2.6.9L17 10l-.9-2.6L13.5 6.5l2.6-.9z M10.5 3.5l.5 1.4 1.4.5-1.4.5-.5 1.4-.5-1.4-1.4-.5 1.4-.5z',
  // A luggage-style label with its eyelet — the semantic tag tool. Deliberately not a price tag or a
  // bookmark: both read as "category" or "saved", and this one means "what this thing is".
  tag: 'M12.5 3H21v8.5l-9.3 9.3a1.6 1.6 0 0 1-2.3 0l-6.2-6.2a1.6 1.6 0 0 1 0-2.3z M17 7v.01',
  plus: 'M12 5v14 M5 12h14',
  upload: 'M12 19V7 M8 11l4-4 4 4 M4 19h16',
  swatch: 'M3 4h8v8H3z M13 4h8v8h-8z M3 14h8v6H3z M13 14h8v6h-8z M6 7.5h2 M16 7.5h2',
  // A map pin — where the site is. Used by the localization summary, and nowhere the *map* itself is
  // meant: this marks the place a garden was told it stands in, not a coordinate on a frame.
  pin: 'M12 21.5s7-6.6 7-11.5a7 7 0 1 0-14 0c0 4.9 7 11.5 7 11.5z M12 7.5a2.7 2.7 0 1 0 0 5.4 2.7 2.7 0 0 0 0-5.4z',
  // A tallied list — the plant manifest. Bulleted rather than checkboxed: it is a count of what is
  // there, not a set of tasks.
  list: 'M9 6h11 M9 12h11 M9 18h11 M4.5 6h.01 M4.5 12h.01 M4.5 18h.01',
  // A clock with the undo arrow wrapped around it — the operations history. Deliberately built from
  // the `undo` arc rather than from a fresh one, so the panel's button reads as the long form of the
  // button beside it.
  history: 'M3.6 12a8.4 8.4 0 1 0 2.7-6.2 M3 4.2v5h5 M12 7.8V12l3.2 1.9',
  // Skip-to-start and skip-to-end, for rewinding the session and running it forward again. Drawn as
  // the transport pair every player uses, because that is exactly what travelling the list is.
  rewind: 'M6 5.5v13 M19 5.5l-10 6.5 10 6.5z',
  forward: 'M18 5.5v13 M5 5.5l10 6.5L5 18.5z',
  // A perched bird in profile — the fauna palette and the fauna history glyph. A bird rather than a
  // paw print or a generic animal: the catalog spans a bumblebee to a deer, and the one silhouette
  // everything in it shares is "a living thing standing on the site". A paw would have claimed
  // mammals; a butterfly would have claimed insects.
  fauna: 'M8.2 5.4a2.1 2.1 0 1 0 0 4.2 2.1 2.1 0 0 0 0-4.2z M10 8.6c3.4.3 6.1 2.2 7.4 5.2 1 2.3.4 4.4-1.9 5.4-2.6 1.1-5.7-.1-7.6-2.4C6 14.4 5.4 11.5 6.3 9.2 M6.6 7h-3 M13.6 15.6l4.6-3.2',
  // A bench in profile — the objects palette. The one fixture nobody misreads, and the class's own
  // canonical example in `docs/PROPS.md`.
  object: 'M3 10h18 M3 13.5h18 M5 10V7.5h14V10 M5.5 13.5V19 M18.5 13.5V19 M3 7.5h18',

  /* ---------------------------------- Site lenses ---------------------------------- */
  // One glyph per lens in the bar at the foot of the design view (see `FloraLens.ts`). Each names a
  // *resource* rather than an animal, because that is what the record actually carries and what the
  // heat map is actually drawing: `fauna` above already claims "wildlife in general", and it is the
  // roll-up lens that reuses it.

  // A three-berry cluster on its stem — bird food. Berries rather than a bird, so it cannot be
  // confused with `fauna`, and because seed and mast read as the same gesture at this size.
  berry:
    'M12 3v4.6 M12 7.6L8.6 11 M12 7.6l3.4 3.4 M12 7.6v7.6 M8.6 11a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8z M15.4 11a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8z M12 15.2a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8z',
  // A bloom face-on with its disc — nectar and pollen. Five petals: four reads as a cross and six as
  // a gear, and the disc in the middle is the part that says "this is where the food is".
  flower:
    'M12 5.2a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2z M16 8.1a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2z M14.5 12.8a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2z M9.5 12.8a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2z M8 8.1a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2z M12 9.8a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4z',
  // A caterpillar — the nursery lens. The larva rather than the butterfly on purpose: the whole point
  // of the lens is that nectar for adults and foliage for young are different questions, and a
  // butterfly glyph would quietly restate the pollinator lens beside it.
  larva:
    'M4 16.5c1.5-3.2 3.3-3.2 4.8 0 1.5-3.2 3.3-3.2 4.8 0 1.5-3.2 3.3-3.2 4.8 0 M19.4 12a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4z M19 11.6l-.8-2.4 M20.6 11.8l1.3-2 M20.1 13.7h.01',
  // A paw — mammal food. Pad and four toes; the one animal mark that is read the same everywhere.
  paw:
    'M12 13.4c3 0 5 1.9 5 3.9s-2.2 2.9-5 2.9-5-.9-5-2.9 2-3.9 5-3.9z M7.4 8.6a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2z M10.6 6.8a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2z M13.4 6.8a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2z M16.6 8.6a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2z',
  // Cut stems standing over winter — insect habitat. The caps are the whole glyph: an open stem end
  // is a nest cavity, and it is the difference between stems left up and stems tidied away.
  stems: 'M3 20h18 M7 20V8 M12 20V5 M17 20V10 M5.6 8h2.8 M10.6 5h2.8 M15.6 10h2.8',
  // A nest cup with two eggs — bird shelter. Cover and nesting are one lens because they are one
  // decision, and the nest is the half of it a client recognises.
  nest:
    'M4.5 12.5c0 4.4 3.4 6.5 7.5 6.5s7.5-2.1 7.5-6.5z M3.5 12.5h17 M9.6 8.2a1.9 2.4 0 1 0 0 4.8 1.9 2.4 0 0 0 0-4.8z M14.4 8.2a1.9 2.4 0 1 0 0 4.8 1.9 2.4 0 0 0 0-4.8z M6.6 15.8h10.8',
  // A stag's head — deer resistance. Antlers carry the whole silhouette; the muzzle alone would be
  // any grazing animal, and browse pressure in a garden means deer specifically.
  deer:
    'M12 14.6c-1.8 0-3 1.4-3 3.1 0 1.6 1.3 2.8 3 2.8s3-1.2 3-2.8c0-1.7-1.2-3.1-3-3.1z M9.3 15L6.6 13.2 M14.7 15l2.7-1.8 M10.3 13.4L8.6 8.5 M8.6 8.5L6.2 9.7 M8.6 8.5L7.7 5.6 M13.7 13.4l1.7-4.9 M15.4 8.5l2.4 1.2 M15.4 8.5l.9-2.9',
} as const;

export type IconName = keyof typeof PATHS;

/** Render an icon as an inline SVG string. */
export function icon(name: IconName, size = 18): string {
  const d = PATHS[name];
  return (
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" aria-hidden="true" ` +
    `stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">` +
    `<path d="${d}"/></svg>`
  );
}

/** Filled variant, used for the "favorited" star. */
export function filledIcon(name: IconName, size = 18): string {
  const d = PATHS[name];
  return (
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="currentColor" aria-hidden="true" ` +
    `stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"><path d="${d}"/></svg>`
  );
}
